import { useEffect, useRef, useState } from 'react';
import { floorPercent } from 'core/common/utils/floorPercents';

const minProgress = 0;
const maxProgress = 100;

type Params = {
  durationMs: number;
  onFinished?: () => void;
};

// TODO: consider ability to use hook useProgress
export const useControlledProgress = ({ durationMs, onFinished }: Params) => {
  const [progress, setProgress] = useState(minProgress);
  const [isPaused, setIsPaused] = useState(false);

  const startTimeMsRef = useRef<number | null>(null);
  const elapsedTimeMsRef = useRef<number>(0);
  const animationFrameIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (isPaused) return;

    const animate = (timestamp: number) => {
      if (startTimeMsRef.current === null) {
        startTimeMsRef.current = timestamp - elapsedTimeMsRef.current;
      }

      const elapsedTimeMs = Math.min(timestamp - startTimeMsRef.current, durationMs);
      elapsedTimeMsRef.current = elapsedTimeMs;

      const newProgress = (elapsedTimeMs / durationMs) * maxProgress;
      setProgress(newProgress);

      const animationIsNotComplete = elapsedTimeMs < durationMs;

      if (animationIsNotComplete) {
        // Continue animation if not complete
        animationFrameIdRef.current = requestAnimationFrame(animate);
        return;
      }

      setProgress(maxProgress);

      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }

      if (onFinished) {
        onFinished();
      }
    };

    animationFrameIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationMs, isPaused]);

  const pauseProgress = () => {
    if (isPaused) return;

    setIsPaused(true);

    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
  };

  const continueProgress = () => {
    if (!isPaused) return;

    setIsPaused(false);
    startTimeMsRef.current = null;
  };

  const resetProgress = () => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
    }

    setProgress(minProgress);
    setIsPaused(false);

    startTimeMsRef.current = null;
    elapsedTimeMsRef.current = 0;
  };

  return {
    progress,
    isPaused,
    pauseProgress,
    resetProgress,
    continueProgress,
    flooredProgress: floorPercent(progress),
  };
};
